<template>
  <div>
    <!-- Address Form - Modal Version -->
    <Modal v-if="!inline" :modalId="addressSearchModalId" size="modal-md">
      <ModalHeader :title="addressFormTitle" icon="fas fa-map-marker-alt">
        <div class="float-right">
          <button
            type="button"
            class="btn btn-icon-button btn-icon-button--gray"
            @click="closeAddressModals()"
            :data-test-hook="'x-button-' + addressSearchModalId"
          >
            <i class="fas fa-times" />
          </button>
        </div>
      </ModalHeader>
      <ModalBody>
        <b-alert :show="isError" class="alert alert-danger">
          <h4 class="alert-heading">{{ $t('xSomethingWentWrong') }}</h4>
          <p class="alert-body">{{ $t('xSomethingIsWrongPleaseContactSupportText') }}</p>
        </b-alert>
        <AddressForm
          ref="addressFormRef"
          :isBillingAddress="isBillingAddress"
          @setIsFormInvalid="setIsFormInvalid"
          @setSubmittedAddress="setSubmittedAddress"
        />
      </ModalBody>
      <ModalFooter>
        <button
          type="button"
          class="btn btn-secondary"
          @click="closeAddressModals()"
          data-test-hook="cancel-address-form-button"
        >
          {{ $t('xCancel') }}
        </button>
        <button
          type="button"
          class="btn btn-primary"
          :disabled="isSearching || isSaving"
          @click="verifyAddress()"
          data-test-hook="submit-address-form-button"
        >
          {{ $t('xContinue') }}
          <b-spinner small v-if="isSearching || isSaving" />
          <i v-else class="fa-solid fa-arrow-right" />
        </button>
      </ModalFooter>
    </Modal>

    <!-- Address Form - Inline Version -->
    <div v-else-if="!showAddressValidationView && inline" class="inline-address-form">
      <b-alert :show="isError" class="alert alert-danger">
        <h4 class="alert-heading">{{ $t('xSomethingWentWrong') }}</h4>
        <p class="alert-body">{{ $t('xWaitAndTryAgainErrorText') }}</p>
      </b-alert>
      <AddressForm
        ref="addressFormRef"
        :isBillingAddress="isBillingAddress"
        @setIsFormInvalid="setIsFormInvalid"
        @setSubmittedAddress="setSubmittedAddress"
      />
      <b-row v-if="showContinueButton" class="mt-3">
        <b-col>
          <button
            type="button"
            class="btn btn-primary btn-block"
            @click="verifyAddress()"
            :disabled="isSearching || isSaving"
            data-test-hook="submit-address-form-button"
          >
            {{ $t('xContinue') }}
            <b-spinner small v-if="isSearching || isSaving" />
            <i v-else class="fa-solid fa-arrow-right" />
          </button>
        </b-col>
      </b-row>
    </div>

    <!-- Address Validation -  Modal Version -->
    <Modal v-if="!inline" :modalId="addressValidationModalId" size="modal-md">
      <ModalHeader :title="$t('xAddressSearchVerifyAddressModalTitle')" icon="fas fa-check-circle">
        <div class="float-right">
          <button
            type="button"
            class="btn btn-icon-button btn-icon-button--gray"
            @click="closeAddressModals()"
            :data-test-hook="'x-button-' + addressValidationModalId"
          >
            <i class="fas fa-times" />
          </button>
        </div>
      </ModalHeader>
      <ModalBody>
        <AddressValidation
          :submittedAddress="submittedAddress"
          :validateAddressResponse="validateAddressResponse"
          @onSelect="handleAddressSelected"
        />
      </ModalBody>
      <ModalFooter>
        <button
          type="button"
          class="btn btn-secondary"
          @click="goBackToAddressForm()"
          data-test-hook="back-to-address-form-button"
        >
          {{ $t('xAddressSearchEditAddress') }}
        </button>
        <button
          type="button"
          class="btn btn-primary"
          @click="$router.push({ path: `/support` })"
          data-test-hook="confirm-address-button"
          v-if="showContactSupportButton"
        >
          {{ $t('xContactSupport') }}
        </button>
        <button
          type="button"
          class="btn btn-primary"
          :disabled="!selectedAddress || isSaving"
          @click="saveAndSubmit"
          data-test-hook="confirm-address-button"
          v-else
        >
          {{ $t('xAddressSearchConfirmAddress') }}
          <b-spinner small v-if="isSaving" />
          <i v-else class="fa-solid fa-arrow-right"></i>
        </button>
      </ModalFooter>
    </Modal>

    <!-- Address Validation - Inline Version -->
    <div v-else-if="showAddressValidationView && inline" class="inline-address-validation">
      <h5 v-if="showAddressCheckmarkHeader" class="mb-3">
        <i class="fas fa-check-circle mr-2"></i>{{ $t('xAddressSearchVerifyAddressModalTitle') }}
      </h5>
      <AddressValidation
        :submittedAddress="submittedAddress"
        :validateAddressResponse="validateAddressResponse"
        @onSelect="handleAddressSelected"
      />
      <b-row v-if="showValidationButtons" class="mt-3">
        <b-col>
          <button
            type="button"
            class="btn btn-secondary btn-block"
            @click="goBackToAddressForm()"
            data-test-hook="back-to-address-form-button"
          >
            {{ $t('xAddressSearchEditAddress') }}
          </button>
        </b-col>
        <b-col v-if="showContactSupportButton">
          <button
            type="button"
            class="btn btn-primary btn-block"
            @click="$router.push({ path: `/support` })"
            data-test-hook="confirm-address-button"
          >
            {{ $t('xContactSupport') }}
          </button>
        </b-col>
        <b-col v-else>
          <button
            type="button"
            class="btn btn-primary btn-block"
            :disabled="!selectedAddress || isSaving"
            @click="saveAndSubmit"
            data-test-hook="confirm-address-button"
          >
            {{ $t('xAddressSearchConfirmAddress') }}
            <b-spinner small v-if="isSaving" />
            <i v-else class="fa-solid fa-arrow-right"></i>
          </button>
        </b-col>
      </b-row>
    </div>
  </div>
</template>

<script>
import AddressForm from './AddressForm.vue'
import AddressValidation from './AddressValidation.vue'

export default {
  components: {
    AddressForm,
    AddressValidation
  },
  emits: ['onSubmit', 'onClose', 'addressSearchModalViewChanged', 'setIsNextDisabled', 'addressSearchError'],
  props: {
    addressFormTitle: String,
    addressSearchModalId: {
      type: String,
      default: 'address-search-form'
    },
    addressValidationModalId: {
      type: String,
      default: 'address-validation'
    },
    inline: {
      type: Boolean,
      default: false
    },
    showContinueButton: {
      type: Boolean,
      default: true
    },
    showValidationButtons: {
      type: Boolean,
      default: true
    },
    showAddressCheckmarkHeader: {
      type: Boolean,
      default: true
    },
    isBillingAddress: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      submittedAddress: null, // the actual address typed by the user
      selectedAddress: null, // the selected, validated address from the radio list (if applicable)
      isFormInvalid: true,
      addressFormRef: null,
      validateAddressResponse: null,
      isSearching: false,
      isSaving: false,
      isError: false,
      showAddressValidationView: false // controls which inline view to show
    }
  },
  methods: {
    openAddressFormModal() {
      this.openModal(this.addressSearchModalId)
      // Ensure modal is fully rendered before showing
      this.$nextTick(() => {
        this.$refs.addressFormRef.onShow()
      })
    },
    setIsFormInvalid(val) {
      this.isFormInvalid = val
      this.$emit('setIsFormInvalid', val)
    },
    setSubmittedAddress(val) {
      this.submittedAddress = val
    },
    async verifyAddress() {
      if (this.isFormInvalid) {
        // trigger all error messages to appear
        this.$refs.addressFormRef?.$v?.$touch()
        return
      }

      this.isSearching = true
      this.isError = false

      const addressRequest = {
        ...this.submittedAddress,
        countryCode: this.submittedAddress.country
      }

      await this.$store
        .dispatch('verifyAddress', addressRequest)
        .then(result => {
          if (result.data) {
            this.validateAddressResponse = result.data?.[0] // returns as array

            // if it's an exact match, take the first result and submit, no verification required
            if (this.validateAddressResponse.isValidated && !this.validateAddressResponse.isChangeRequired) {
              this.isSaving = true
              this.selectedAddress = this.validateAddressResponse.suggestionList[0]
              this.saveAndSubmit()
            } else {
              this.openAddressValidationModal()
            }
          }
        })
        .catch(() => {
          this.isError = true
        })
        .finally(() => {
          this.isSearching = false
        })
    },
    saveAndSubmit() {
      this.isSaving = true
      if (this.selectedAddress) {
        const responseOptions = {
          openAddressFormWithError: () => {
            this.isSaving = false
            this.isError = true
            this.isSearching = false
            this.goBackToAddressForm()
          },
          closeAddressModals: () => {
            this.isSaving = false
            this.isError = false
            this.isSearching = false
            this.closeAddressModals()
          }
        }
        this.$emit('onSubmit', this.selectedAddress, responseOptions)
      }
    },

    openAddressValidationModal() {
      this.isSaving = false
      if (this.inline) {
        this.showAddressValidationView = true
        this.$emit('addressSearchModalViewChanged', true)
      } else {
        this.closeModal(this.addressSearchModalId)
        this.openModal(this.addressValidationModalId)
      }
    },
    goBackToAddressForm() {
      if (this.inline) {
        this.showAddressValidationView = false
        this.$emit('addressSearchModalViewChanged', false)
        // Populate form with the user's originally submitted address
        this.$nextTick(() => {
          if (this.submittedAddress && this.$refs.addressFormRef) {
            // Transform submittedAddress format to match what populateWithAddress expects
            const addressForForm = {
              country: this.submittedAddress.country,
              line1: this.submittedAddress.address1,
              line2: this.submittedAddress.address2,
              city: this.submittedAddress.city,
              region: this.submittedAddress.state,
              postalCode: this.submittedAddress.postalCode
            }
            this.$refs.addressFormRef.populateWithAddress(addressForForm)
          }
        })
      } else {
        this.closeModal(this.addressValidationModalId)
        this.openModal(this.addressSearchModalId)
        // Ensure modal is fully rendered before showing
        this.$nextTick(() => {
          this.$refs.addressFormRef.onShow()
        })
      }
    },
    handleAddressSelected(address) {
      this.selectedAddress = address
    },

    closeAddressModals() {
      if (!this.inline) {
        this.closeModal(this.addressSearchModalId)
        this.closeModal(this.addressValidationModalId)
        this.resetModalState()
      }
      // For inline mode, don't reset state to avoid flash before navigation
      this.$emit('onClose')
    },
    resetModalState() {
      // Reset showAddressValidationView first so AddressForm re-renders if needed
      const wasShowingValidationView = this.showAddressValidationView
      this.showAddressValidationView = false
      if (this.inline && wasShowingValidationView) {
        this.$emit('addressSearchModalViewChanged', false)
      }
      this.submittedAddress = null
      this.validateAddressResponse = null
      this.selectedAddress = null
      this.isSearching = false

      // Wait for DOM update before resetting form
      this.$nextTick(() => {
        if (this.$refs.addressFormRef) {
          this.$refs.addressFormRef.resetForm()
        }
      })
    }
  },
  computed: {
    showContactSupportButton() {
      return (
        this.validateAddressResponse &&
        !this.validateAddressResponse?.isValidated &&
        !this.validateAddressResponse?.suggestionList?.length
      )
    },
    // Expose state for parent components
    isNextDisabled() {
      return this.isSearching || this.isSaving
    }
  },
  watch: {
    isNextDisabled: {
      immediate: true,
      handler(newVal) {
        this.$emit('setIsNextDisabled', newVal)
      }
    },
    isError: {
      immediate: true,
      handler(newVal) {
        this.$emit('addressSearchError', newVal)
      }
    }
  }
}
</script>
<style lang="scss">
.alert-body {
  font-weight: 400;
}
</style>
